import React, { useState, useEffect, useRef, Suspense, lazy } from "react";
import ContactLog from "./ContactLog";
import MaxBidCalculator from "./MaxBidCalculator";
import { DEAL_STAGES, DealStage, isDealStage } from "../lib/dealStages";
import RecordedDeedNotice from "./RecordedDeedNotice";
import LienStack from "./LienStack";
import { Property, PartyView } from "../types";
import {
  buildZillowUrl,
  buildZillowLabel,
  buildRedfinUrl,
  buildStreetViewUrl,
  buildGoogleMapsUrl,
  buildPolarisUrl,
  buildSpatialEstUrl,
} from "../lib/propertyUrls";
import { propertyClassLabel } from "../lib/propertyLabels";
import { resolvePropertyType, dataCompleteness, isDeliverableAddress, resolveDisplayAddress, resolveDisplayParcelId, resolvePlayType, ownerHoldText, resolveAssessedValue, resolveMarketValue, resolveAmountOwed, resolvePropertyBadge } from "../lib/dataQuality";
import { formatAddedDate, formatCurrency as sharedFormatCurrency, formatDate as sharedFormatDate } from "../lib/format";
import { useOwnerPortfolio } from "../hooks/useOwnerPortfolio";
import { usePortalCase } from "../hooks/usePortalCase";
import { apiClient } from "../lib/apiClient";
import DisclaimerBanner from "./DisclaimerBanner";
import RiskFlagList from "./RiskFlagList";
import BiddersPanel from "./BiddersPanel";
import LinkedCasesSection from "./LinkedCasesSection";
import OtherSignalPills from "./OtherSignalPills";

// Lazy-load heavy panels — defers their JS bundle download to the moment
// the modal first opens, eliminating the ~329ms synchronous mount cost
// measured during the 2026-05-30 perf audit. Each panel lands in its own
// webpack chunk; Suspense provides the loading boundary.
const BidTimeline = lazy(() => import("./BidTimeline"));
const BidIntelligencePanel = lazy(() => import("./BidIntelligencePanel"));
const DealAnalyzer = lazy(() => import("./DealAnalyzer"));
const OwnerContactsPanel = lazy(() => import("./OwnerContactsPanel"));
const BidHistoryGate = lazy(() => import("./BidHistoryGate"));

// StatusHelpPopover — click-to-toggle help bubble next to the status badge.
// Replaces the previous `<span title="...">` (#183), which only showed on
// native hover and was reported as "fires a click event but produces no
// visible result." Now: clickable, keyboard-accessible (Enter/Space), and
// dismissible by clicking outside or pressing Escape.
// partyRoleRank orders the parties table so the case principals (plaintiff,
// then defendants) come first, upset bidders next, attorneys last, unknown-role
// (legacy rows with no captured role) at the bottom.
function partyRoleRank(role: string): number {
  const r = role.toLowerCase();
  if (r.includes("plaintiff")) return 0;
  if (r.includes("defendant")) return 1;
  if (r.includes("bidder") || r.includes("purchaser")) return 2;
  if (r.includes("attorney")) return 4;
  if (r === "") return 5;
  return 3;
}

// sortParties returns a role-grouped copy (stable within a role) for the table.
function sortParties(parties: PartyView[]): PartyView[] {
  return parties
    .map((p, i) => ({ p, i }))
    .sort((a, b) => partyRoleRank(a.p.role) - partyRoleRank(b.p.role) || a.i - b.i)
    .map((x) => x.p);
}

const PARTIES_COLLAPSED_COUNT = 6;

const StatusHelpPopover: React.FC<{ desc: string }> = ({ desc }) => {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  // Close on click-outside.
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape (when open).
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [open]);

  return (
    <span className="status-help-wrap" ref={wrapRef}>
      <button
        type="button"
        className="status-help"
        aria-expanded={open}
        aria-label="Show status explanation"
        onClick={() => setOpen((v) => !v)}
      >
        ?
      </button>
      {open && (
        <span className="status-help-popover" role="tooltip">
          {desc}
        </span>
      )}
    </span>
  );
};

interface PropertyDetailProps {
  property: Property;
  authed: boolean;
  isWatched: boolean;
  isPro: boolean;
  // isAllowedCounty — geography-aware tier gate (Free/Scout redesign). True
  // for Pro/team/admin, or when this property's county is one of the
  // viewer's chosen counties. Gates Assessed Value below — bid history
  // stays isPro-only (separate 7-day-delay mechanism, not this gate).
  isAllowedCounty: boolean;
  onRequirePro: () => void;
  // See PropertyModal's onPickCounty.
  onPickCounty?: () => void;
  getNotes: (propertyId: string) => string;
  updateNotes: (propertyId: string, notes: string) => Promise<void>;
  // Deal stage (#3075). Optional: omitted, no stage selector renders.
  getStage?: (propertyId: string) => DealStage | null;
  setStage?: (propertyId: string, stage: DealStage | null) => void;
  // activeLeadTypeFilter — the lead-type filter currently applied to the list
  // this property was opened from (e.g. "mortgage_foreclosure"), so the
  // headline badge can lead with the signal the viewer actually filtered for
  // instead of the fixed-priority default. See resolvePropertyBadge. Optional
  // — a caller that doesn't pass it (e.g. opened via a direct/share link with
  // no active filter) just gets the original priority-ranked behavior.
  activeLeadTypeFilter?: string | string[];
}

// Delegates to lib/format.ts (#1033). `treatZeroAsEmpty` preserves the
// original `!value` guard: an assessed value / amount due of literally $0
// means "we don't have this data" on this panel, not a real zero.
function formatCurrency(value: number): string {
  return sharedFormatCurrency(value, { fallback: "N/A", treatZeroAsEmpty: true });
}

// Delegates to lib/format.ts (#1033).
function formatDate(dateStr: string | null | undefined): string {
  return sharedFormatDate(dateStr, "N/A");
}

export interface StalenessInfo {
  daysSince: number;
  saleDatePassedUnconfirmed: boolean;
  isStale: boolean;
}

// computeStaleness backs the #934-item-3 staleness warning: "auction
// scheduled" is a claim that can go quietly wrong (postponement, redemption,
// a bankruptcy stay) with nothing currently re-checking it, and proximity-
// based recheck scheduling (the real fix) is blocked on #932's sale_date
// recovery. Until then, telling the user how stale our last confirmation
// actually is is the honest interim signal. Two INDEPENDENT triggers:
//   - flat staleness: it's just been a long time, regardless of sale_date.
//   - the stronger case: the sale date this status implies has already
//     passed and we have not re-checked the source SINCE that date — the
//     record could not possibly reflect what actually happened at the sale.
// `now` is injectable so tests aren't wall-clock-dependent.
// The flat threshold is 14 days, tightened to 3 when the sale is within the
// next 7 (proximity-aware recheck, #934 item 1's display half). Measured on
// prod 2026-09-25: of 96 auction_scheduled rows with a sale in the next 7
// days, 25 had gone more than 3 days without a source check and only 10 more
// than 14 — the flat threshold hid 15 of the 25. A sale days away is exactly
// when an un-rechecked postponement costs the subscriber a trip. Both numbers
// are judgment calls, not measured constants — tune if they fire too often.
// Pass staleDaysThreshold explicitly to override both.
export const STALE_DAYS_DEFAULT = 14;
export const STALE_DAYS_IMMINENT = 3;
export const IMMINENT_SALE_DAYS = 7;

export function computeStaleness(
  lastVerified: Date | null,
  saleDate: Date | null,
  now: Date = new Date(),
  staleDaysThreshold?: number,
): StalenessInfo | null {
  if (!lastVerified) return null;
  if (staleDaysThreshold === undefined) {
    const daysToSale =
      saleDate && !isNaN(saleDate.getTime())
        ? (saleDate.getTime() - now.getTime()) / 86400000
        : null;
    staleDaysThreshold =
      daysToSale !== null && daysToSale >= 0 && daysToSale <= IMMINENT_SALE_DAYS
        ? STALE_DAYS_IMMINENT
        : STALE_DAYS_DEFAULT;
  }
  const daysSince = Math.floor((now.getTime() - lastVerified.getTime()) / 86400000);
  const saleDatePassedUnconfirmed =
    !!saleDate && !isNaN(saleDate.getTime()) && saleDate < now && saleDate > lastVerified;
  return {
    daysSince,
    saleDatePassedUnconfirmed,
    isStale: daysSince > staleDaysThreshold || saleDatePassedUnconfirmed,
  };
}

// stalenessWarningApplies picks the statuses that get the "last verified N days
// ago" warning: the ones whose claim about a sale goes quietly wrong when nothing
// re-checks the court record.
//   - auction_scheduled: an un-rechecked postponement, redemption or bankruptcy
//     stay makes it confidently WRONG, not merely stale (the original #934 scope).
//   - pending_confirmation: "sale held, awaiting confirmation" silently outlives
//     the confirmation, a resale or a set-aside. Measured 2026-09-14: 285 of the
//     notice-sourced pending_confirmation rows had a sale more than 12 days
//     earlier, and the portal lookup that would update them failed every run
//     from 2026-09-10 to 09-13.
export function stalenessWarningApplies(status: string): boolean {
  return status === "auction_scheduled" || status === "pending_confirmation";
}

// URL helpers (buildZillowUrl, buildZillowLabel, buildPolarisUrl,
// buildSpatialEstUrl) live in ../lib/propertyUrls so vacant-land Zillow
// gating stays in one place.

// ─── SpatialEst building-details helpers ────────────────────────────

// Format lot size — prefer "0.24 ac (10,454 sq ft)" when both are
// present, fall back to whichever is available.
function formatLotSize(acres: number | null, sqft: number | null): string | null {
  if (acres !== null && sqft !== null) {
    return `${acres.toFixed(2)} ac (${Math.round(sqft).toLocaleString()} sq ft)`;
  }
  if (acres !== null) return `${acres.toFixed(2)} ac`;
  if (sqft !== null) return `${Math.round(sqft).toLocaleString()} sq ft`;
  return null;
}

// Format a bed/bath count — 3 beds, 2 full baths, 1 half bath
function formatBedBath(beds: number | null, full: number | null, half: number | null): string | null {
  const parts: string[] = [];
  if (beds !== null && beds > 0) parts.push(`${beds} bd`);
  if (full !== null && full > 0) parts.push(`${full} ba`);
  if (half !== null && half > 0) parts.push(`${half} half`);
  return parts.length > 0 ? parts.join(" · ") : null;
}

function buildOdysseyUrl(): string {
  return "https://portal-nc.tylertech.cloud/Portal/Home/Dashboard/29";
}

// NC eCourts/Odyssey case numbers look like 25CV049809-590 / 25SP001585-480
// (YY + CV|SP|CVD|CVS + digits, optional -AOC suffix). Gaston publishes the
// old county Sheriff format "25 M 388", which Odyssey can't find — so we only
// render the Odyssey link for real Odyssey-format numbers (#492). Mirrors the
// backend regex in internal/courts/case_number.go.
function isOdysseyFormat(caseNo: string | undefined): boolean {
  if (!caseNo) return false;
  return /\d{2}(CV|SP|CVD|CVS)\d{5,7}(-\d{3})?/i.test(caseNo.replace(/\s+/g, ""));
}

// Status label/color/desc lookups now live in lib/statusMeta.ts (PR-A
// of the 2026-04-30 redesign). When a new backend status lands — e.g.
// `postponed`, `auction_passed` — add it to STATUS_META there, not
// here. Three-place duplication was the bug this consolidation fixes.

// Per-class color is local to PropertyDetail (badge styling). The display
// label itself lives in lib/propertyLabels (imported at top) so
// PortfolioView (#184) and any future surface share the same strings.
// Don't re-introduce a duplicate {label, color} map here.
const propertyClassColors: Record<string, string> = {
  land_only: "#0ea5e9",
  residential: "#22c55e",
  commercial: "#8b5cf6",
};

const PropertyDetail: React.FC<PropertyDetailProps> = ({
  property,
  authed,
  isWatched,
  isPro,
  isAllowedCounty,
  onRequirePro,
  onPickCounty,
  getNotes,
  updateNotes,
  getStage,
  setStage,
  activeLeadTypeFilter,
}) => {
  // Out-of-area fields: a viewer with no chosen county yet is sent to the
  // county picker (their free county is the fix), everyone else to the upgrade.
  const handleOutsideAreaClick = () => (onPickCounty ? onPickCounty() : onRequirePro());
  const [notes, setNotes] = useState("");
  const [notesDirty, setNotesDirty] = useState(false);

  // Owner-portfolio signal (Thread B): other tracked properties with the same
  // owner. Lazily fetched; renders only when there's at least one other parcel.
  const { portfolio: ownerPortfolio } = useOwnerPortfolio(property.id);
  // Court case is fetched for the drawer rather than shipped in the list (load-time fix).
  const portalCase = usePortalCase(property);

  useEffect(() => {
    if (isWatched) setNotes(getNotes(property.id));
  }, [isWatched, property.id, getNotes]);

  const saveNotes = () => {
    updateNotes(property.id, notes);
    setNotesDirty(false);
  };
  // Headline badge: the lifecycle STATUS for a live auction, or the
  // highest-priority LEAD TYPE for a pre-auction lead — see
  // resolvePropertyBadge for the full rule (mirrors PropertyCard).
  const badge = resolvePropertyBadge(property, activeLeadTypeFilter);
  // Coordinate links only for rows the viewer's plan opens in full: out-of-area
  // rows carry offset coordinates, and Street View of the wrong house misleads.
  const streetViewUrl = isAllowedCounty ? buildStreetViewUrl(property) : null;
  const mapsUrl = isAllowedCounty ? buildGoogleMapsUrl(property) : null;

  const saleDate = property.sale?.saleDate;
  const upsetDeadline = property.sale?.upsetWindowCloseAt;
  // Computed from the docket because no firm published a close date (#3267).
  const upsetDeadlineEstimated = property.sale?.upsetWindowDerived === true;
  // court_file only ever aliased court_file_number and matches 0 prod rows,
  // so the old `court_file_number || court_file` alternation is gone (#1872).
  const courtFile = property.courtFileNumber;
  const source = property.source;
  // NUMBERS now, not strings — the server parses them (#1872). Every guard
  // below is `!= null` rather than truthiness: as strings a "0" bid was
  // truthy, so a real zero would silently vanish under a falsy check.
  const openingBid = property.sale?.openingBid;
  const currentBid = property.sale?.currentBid;
  // Cabarrus provides only deed legal descriptions (e.g. "LT 28 KENSINGTON
  // FOREST"), not postal addresses. When present, suppress the normal address
  // rendering and the Zillow link (which would produce a garbage search). (#488)
  const legalDesc = property.situs?.legalDescription ?? null;
  // A point-in-polygon-derived NG911 situs (NC OneMap AddressNC) wins over the
  // legal description when present — that's a real street address for an
  // otherwise legal-description-only county (Cabarrus). See CabarrusSitusOverlay.
  const displayAddr = resolveDisplayAddress(property);

  // Percent premium over the opening bid. The separate parsed copies this
  // used to keep (openingBid/currentBid) are gone — the bids ARE numbers now.
  const premiumPct =
    openingBid && currentBid && openingBid > 0 && currentBid > openingBid
      ? Math.round(((currentBid - openingBid) / openingBid) * 100)
      : null;

  const [copied, setCopied] = useState(false);
  const handleCopyCaseNumber = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!courtFile) return;
    navigator.clipboard.writeText(courtFile).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };

  // Share button (#129). Pro users get a tokenized public link
  // (/s/<token>, 24h expiry) the recipient can view without an account.
  // Free users keep the in-app deep-link (/app?property=<id>) — recipient
  // must sign in. The Pro upgrade path is the value differentiator.
  //
  // AUDIT (#677): this feature isn't mentioned anywhere in Pricing.tsx's
  // TIERS copy for any tier, so whether Scout should get the tokenized
  // link (today it does — `isPro` includes scout) or not is genuinely
  // ambiguous. Left as-is rather than guessing; flagged in the #677 PR
  // description for a founder call.
  const [linkCopied, setLinkCopied] = useState(false);
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState<string | null>(null);
  // Court Case section: the action timeline is collapsed by default (it can be
  // long and ate the card), and a big party list collapses to the first few.
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [showAllParties, setShowAllParties] = useState(false);
  const handleCopyShareLink = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShareError(null);
    if (isPro) {
      setShareLoading(true);
      try {
        const { data } = await apiClient.post<{ url: string; expiresAt: string }>(
          `/properties/${property.id}/share`
        );
        await navigator.clipboard.writeText(data.url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch (err: any) {
        setShareError("Couldn't generate share link. Please retry.");
        setTimeout(() => setShareError(null), 3000);
      } finally {
        setShareLoading(false);
      }
      return;
    }
    // Free fallback: in-app deep link (recipient signs in to view).
    const url = `${window.location.origin}/app?property=${property.id}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 1500);
    });
  };

  // Show the badge whenever the row has a known propertyClass — falls back
  // gracefully for unknown enum values (Title Cased) so the badge never
  // shows a raw snake_case key. Color stays nil-safe via the local map.
  const propClass = property.propertyClass || "";
  const propClassColor = propertyClassColors[propClass];

  return (
    <div className="panel-body">
      <div className="status-badge-row">
        <div className="status-badge" style={{ backgroundColor: badge.color }}>
          {badge.label}
        </div>
        {badge.desc && <StatusHelpPopover desc={badge.desc} />}
        {badge.foreclosureTypeLabel && (
          <span
            className="lrow-pill lrow-pill--muted"
            style={
              badge.foreclosureTypeColor
                ? { color: badge.foreclosureTypeColor, borderColor: `${badge.foreclosureTypeColor}40` }
                : undefined
            }
            title="Foreclosure type — mortgage and tax foreclosures follow different NC procedures"
          >
            {badge.foreclosureTypeLabel}
          </span>
        )}
        {(property.isTimeshare || property.linkedCases?.isTimeshare) && (
          <span
            className="lrow-pill lrow-pill--muted"
            title="Timeshare resort — each foreclosure case here is one owner's interval (unit + week), not the whole property"
          >
            Timeshare
          </span>
        )}
        <OtherSignalPills labels={badge.otherSignalLabels} />
        {propClass && (() => {
          // Conflict-aware: reuses resolvePropertyType's cross-check against
          // the physical structure (beds/sqft/year/has_structure) instead of
          // presenting the raw property_class as an undisputed fact. Catches
          // both directions — land_only with a structure on record, and
          // residential with none — the same logic the data-completeness
          // warning below already uses, now also applied to this badge
          // instead of leaving it as the one place in the UI that still
          // shows the raw, unqualified label.
          const rt = resolvePropertyType(property);
          return (
            <div
              className="property-class-badge"
              style={propClassColor ? { backgroundColor: propClassColor } : undefined}
              title={rt.conflict && rt.note ? rt.note : undefined}
            >
              {propertyClassLabel(propClass)}
              {rt.conflict && (
                <span className="property-class-badge__conflict" aria-hidden="true">
                  {" "}⚠
                </span>
              )}
            </div>
          );
        })()}
        <button
          className="share-link-btn"
          onClick={handleCopyShareLink}
          disabled={shareLoading}
          title={
            isPro
              ? "Copy a public 24-hour share link (no sign-in required)"
              : "Copy in-app link (recipient needs an account)"
          }
        >
          {shareLoading
            ? "…"
            : linkCopied
            ? isPro
              ? "✓ Public link copied"
              : "✓ Copied"
            : isPro
            ? "🔗 Share (24h)"
            : "🔗 Share"}
        </button>
        {shareError && <span className="share-link-error">{shareError}</span>}
      </div>

      {/* #2545: county Register-of-Deeds evidence ("Foreclosure deed recorded
          <date>"). Silent when absent/locked. Never alters the status badge. */}
      <RecordedDeedNotice property={property} />

      {/* Lien master plan WS6 (#2966): liens matched by owner name + honest per-class
          coverage. Evidence only; silent while loading/locked/error. */}
      <LienStack property={property} />

      {/* Key facts strip (2026-08-21 UX pass) — the facts that drive an
          auction buyer's next action (sale date, upset window + countdown,
          the money number) surfaced at the very top instead of several
          hundred lines down in Key Dates / Bid Activity. Those sections
          remain below as the detail record. Amber is the design system's
          reserved upset-urgency cue (DESIGN_SYSTEM §2). Min-next-bid math
          mirrors PropertyCard: greater of +5% or +$750 (G.S. 1-339.25). */}
      {(() => {
        const dateRe = /\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2}/;
        const rawSaleIsReal = !!saleDate && dateRe.test(saleDate);
        const saleDisplay = rawSaleIsReal
          ? saleDate
          : property.auctionDate
          ? formatDate(property.auctionDate)
          : null;
        const deadlineDate = upsetDeadline ? new Date(upsetDeadline) : null;
        const deadlineValid = !!(deadlineDate && !isNaN(deadlineDate.getTime()));
        const startOfToday = new Date();
        startOfToday.setHours(0, 0, 0, 0);
        // Calendar-day distance (both sides midnight-anchored) so a 5:00 PM
        // close time doesn't inflate the count: a deadline 5 days out reads
        // "5 days left", the close date itself reads "closes today".
        const startOfDeadlineDay = deadlineValid ? new Date(deadlineDate!) : null;
        if (startOfDeadlineDay) startOfDeadlineDay.setHours(0, 0, 0, 0);
        const daysLeft = startOfDeadlineDay
          ? Math.round((startOfDeadlineDay.getTime() - startOfToday.getTime()) / 86400000)
          : null;
        const windowOpen = daysLeft !== null && daysLeft >= 0;
        const minNextBid =
          currentBid != null ? currentBid + Math.max(currentBid * 0.05, 750) : null;
        if (!saleDisplay && !upsetDeadline && openingBid == null && currentBid == null) {
          return null;
        }
        return (
          <div className="key-facts-strip">
            {saleDisplay && (
              <div className="key-fact">
                <span className="key-fact__label">Sale date</span>
                <span className="key-fact__value">{saleDisplay}</span>
              </div>
            )}
            {upsetDeadline && (
              <div className="key-fact">
                <span className="key-fact__label">Upset window</span>
                <span className="key-fact__value">{upsetDeadline}</span>
                {upsetDeadlineEstimated && (
                  <span className="key-fact__note" title="Estimated from the court docket: 10 days after the latest bid or report of sale, past weekends and court holidays. Confirm with the clerk before relying on it.">
                    estimated from court docket
                  </span>
                )}
                {windowOpen ? (
                  <span className="key-fact__pill key-fact__pill--urgent">
                    {daysLeft === 0
                      ? "closes today"
                      : daysLeft === 1
                      ? "closes tomorrow"
                      : `${daysLeft} days left`}
                  </span>
                ) : deadlineValid ? (
                  <span className="key-fact__pill key-fact__pill--closed">
                    window closed
                  </span>
                ) : null}
              </div>
            )}
            {windowOpen && minNextBid != null ? (
              <div className="key-fact">
                <span className="key-fact__label">Min next bid</span>
                <span className="key-fact__value">{sharedFormatCurrency(minNextBid)}</span>
              </div>
            ) : currentBid != null ? (
              <div className="key-fact">
                <span className="key-fact__label">Current bid</span>
                <span className="key-fact__value">{sharedFormatCurrency(currentBid)}</span>
              </div>
            ) : openingBid != null ? (
              <div className="key-fact">
                <span className="key-fact__label">Opening bid</span>
                <span className="key-fact__value">{sharedFormatCurrency(openingBid)}</span>
              </div>
            ) : null}
          </div>
        );
      })()}

      {/* Data-completeness warning — surfaces missing core fields + cross-field
          conflicts so a thin or contradictory row reads honestly instead of as
          a confident blank ("never mislead"). Hidden when the row is complete. */}
      {(() => {
        const dq = dataCompleteness(property);
        if (!dq.warn) return null;
        return (
          <div className="data-warning" role="status">
            <span className="data-warning-icon" aria-hidden="true">⚠</span>
            <span className="data-warning-body">
              {dq.sourceLimitedNote ? (
                <span>{dq.sourceLimitedNote}</span>
              ) : (
                dq.missing.length > 0 && (
                  <span>Limited data — missing {dq.missing.join(", ")}.</span>
                )
              )}
              {dq.conflicts.map((c, i) => (
                <span key={i} className="data-warning-conflict">{c}</span>
              ))}
            </span>
          </div>
        );
      })()}

      {/* Staleness warning (#934 item 3) — see computeStaleness's doc comment
          and stalenessWarningApplies for which statuses get it. */}
      {(() => {
        if (!stalenessWarningApplies(property.status)) return null;
        // The server already takes the max of the three enrichment stamps
        // (#1872), so the local mostRecentTimestamp helper is gone.
        const lastVerified = property.lastVerifiedAt ? new Date(property.lastVerifiedAt) : null;
        const saleDateObj = saleDate ? new Date(saleDate) : null;
        // Never-verified is a third state, not "fresh": absence of a check
        // must not read as a confident badge (#934 item 2's three-state shape).
        if (!lastVerified) {
          return (
            <div className="data-warning" role="status">
              <span className="data-warning-icon" aria-hidden="true">⚠</span>
              <span className="data-warning-body">
                Not yet re-verified against the court record. This status may be out of date.
              </span>
            </div>
          );
        }
        const staleness = computeStaleness(lastVerified, saleDateObj);
        if (!staleness || !staleness.isStale) return null;
        return (
          <div className="data-warning" role="status">
            <span className="data-warning-icon" aria-hidden="true">⚠</span>
            <span className="data-warning-body">
              Last verified {staleness.daysSince} {staleness.daysSince === 1 ? "day" : "days"} ago
              {staleness.saleDatePassedUnconfirmed
                ? " — before the scheduled sale date. The sale may have occurred, been postponed, or been withdrawn; this status hasn't been re-confirmed since."
                : ". This status may be out of date."}
            </span>
          </div>
        );
      })()}

      {/* Risk flags (#630) — automated risk-intelligence signals grounded in NC
          statute/case law (federal tax liens, surviving taxes, title
          insurability, …). The DisclaimerBanner above makes clear these are
          signals, not a title search or legal advice. RiskFlagList fetches
          /properties/:id/flags and renders nothing on empty/error, so this
          whole section silently collapses when a property has no flags yet. */}
      <DisclaimerBanner />
      <RiskFlagList propertyId={property.id} />

      {/* Address — Cabarrus sources provide legal descriptions, not postal
          addresses. Show a "Legal Description" group with a clarifying note
          so users aren't confused by deed text in an address field. (#488) */}
      <div className="detail-group">
        <label>{!displayAddr.isDerivedSitus && legalDesc ? "Legal Description" : "Address"}</label>
        {displayAddr.isDerivedSitus ? (
          <>
            <p>{displayAddr.text}</p>
            <p className="secondary">
              {[displayAddr.city, displayAddr.zip].filter(Boolean).join(" ")}
              {displayAddr.city || displayAddr.zip ? " · " : ""}
              Address located via NC OneMap address points
            </p>
          </>
        ) : legalDesc ? (
          <>
            <p>{legalDesc}</p>
            <p className="secondary">No street address available from source</p>
          </>
        ) : (
          <>
            <p>{property.address || "N/A"}</p>
            {/* Many county sources publish a street name without a house
                number (or no address at all). Without a note the row reads as
                broken; tell the user it's a source gap and how to resolve it. */}
            {(() => {
              const addr = (property.address || "").trim();
              // "Look it up by Parcel ID" is only actionable advice when a
              // real, county-issued Parcel ID exists — for the synthetic
              // CASE: placeholder (Brock & Scott, historical Kania) there is
              // no parcel number to search county GIS by.
              const hasRealParcelId = resolveDisplayParcelId(property).value !== null;
              if (!addr) {
                return (
                  <p className="secondary">
                    Street address not published by the source
                    {hasRealParcelId ? " — look it up by Parcel ID in county GIS." : "."}
                  </p>
                );
              }
              if (!/\d/.test(addr)) {
                return (
                  <p className="secondary">
                    House number not published by the source
                    {hasRealParcelId ? " — search by Parcel ID for the exact location." : "."}
                  </p>
                );
              }
              return null;
            })()}
            {(property.city || property.zipCode) && (
              <p className="secondary">
                {[property.city, property.state, property.zipCode]
                  .filter(Boolean)
                  .join(", ")}
              </p>
            )}
          </>
        )}
      </div>

      {/* Parcel ID — a synthetic CASE:{court_file_number} placeholder (used
          when the source publishes no real parcel number, e.g. Brock &
          Scott / historical Kania — #687 / ADR 0023) is never shown under
          this label; it's the same court file number rendered a few
          sections below and mislabeling it as a parcel number reads as a
          data error. */}
      <div className="detail-group">
        <label>Parcel ID</label>
        {(() => {
          const resolved = resolveDisplayParcelId(property);
          return resolved.value !== null ? (
            <p>{resolved.value}</p>
          ) : (
            <p className="secondary">Unavailable — {resolved.note}</p>
          );
        })()}
      </div>

      {/* Property Type — reconciled across property_type / property_class /
          physical structure (resolvePropertyType), so a mislabeled row (e.g. a
          1,557 sqft house tagged "Commercial") shows the honest type with a
          note when sources disagreed. Condo parcels (#106) still surface the
          unit number parsed from the legal description. */}
      {(() => {
        const t = resolvePropertyType(property);
        if (!t.label) return null;
        const isCondo = t.label === "Condominium";
        const legal = property.situs?.legalDescription ?? null;
        // Extract the unit number from legal descriptions like
        // "UNIT 12 U/F 224" or "UNIT 4A". Falls back gracefully when
        // the assessor uses a non-standard format.
        const unitMatch = isCondo && legal ? legal.match(/UNIT\s+(\S+)/i) : null;
        const unitLabel = unitMatch ? `Unit ${unitMatch[1]}` : null;

        // Investor signals from the OneMap value split (Thread A): a play-type
        // tag (Vacant Land / Land-Value Play) and a rough owner hold-duration.
        const playType = resolvePlayType(property);
        const holdText = ownerHoldText(property);

        return (
          <div className="detail-group">
            <label>Property Type</label>
            <p>{t.label}</p>
            {unitLabel && <p className="secondary">{unitLabel}</p>}
            {t.conflict && t.note && (
              <p className="secondary type-conflict">{t.note}</p>
            )}
            {playType && (
              <p className="secondary">
                <span className="detail-playtype">{playType.label}</span> — {playType.note}
              </p>
            )}
            {holdText && <p className="secondary">{holdText} (last recorded sale)</p>}
          </div>
        );
      })()}

      {/* When Parcely first added this row (createdAt). Deliberately not
          labelled "Filed": it is first-ingest time, so a status change does
          not move it and it can post-date the actual court/tax filing. */}
      {formatAddedDate(property.createdAt) && (
        <div className="detail-group">
          <label>Added</label>
          <p>{formatAddedDate(property.createdAt)}</p>
          <p className="secondary">First seen on Parcely — not the filing date</p>
        </div>
      )}

      {/* Owner of record + mailing address — present only for entitled (Pro +
          in-plan) viewers; the backend omits property.owner entirely for
          everyone else, so no client-side gating is needed beyond checking
          it exists. Was already on the list-row card (PropertyCard.tsx) but
          missing here on the detail view — the deeper surface where a user
          most needs the direct-mail payload. Distinct from OwnerContactsPanel
          below: this is the tax-roll owner_name/mailing (parcel_facts,
          ~97% coverage); that panel is phone/email skip-trace enrichment
          from a separate, much sparser table. */}
      {property.owner?.name && (
        <div className="detail-group">
          <label>Owner</label>
          <p>{property.owner.name}</p>
          {(property.owner.mailingAddress || property.owner.mailingCity) && (
            <p className="secondary">
              {[
                property.owner.mailingAddress,
                [property.owner.mailingCity, property.owner.mailingState].filter(Boolean).join(", "),
                property.owner.mailingZip,
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
          {(property.owner.isAbsentee || property.owner.isEntityOwner) && (
            <p className="secondary">
              {[
                property.owner.isAbsentee && "Absentee — mailing address differs from the property",
                property.owner.isEntityOwner && "Entity owner — LLC, company, or trust",
              ]
                .filter(Boolean)
                .join(" · ")}
            </p>
          )}
        </div>
      )}

      {/* Owner-portfolio signal (Thread B) — other tracked properties with the
          same owner in this county. Counts only (no name/contact; owner identity
          stays Pro-gated in the leads view). Renders only when there's a link. */}
      {ownerPortfolio && ownerPortfolio.totalTracked > 0 && (
        <div className="detail-group owner-portfolio">
          <label>Owner Portfolio</label>
          <p>
            This owner is linked to <strong>{ownerPortfolio.totalTracked}</strong> other tracked{" "}
            {ownerPortfolio.totalTracked === 1 ? "property" : "properties"} in this county
            {ownerPortfolio.inForeclosure > 0 && (
              <> — <strong>{ownerPortfolio.inForeclosure}</strong> in active foreclosure</>
            )}
            .
          </p>
          {ownerPortfolio.mailingCorroborated > 0 && (
            <p className="secondary">
              <strong>{ownerPortfolio.mailingCorroborated}</strong> of those also share this
              owner's mailing address — a stronger signal than name alone.
            </p>
          )}
          <p className="secondary">
            A portfolio / serial owner — often a distressed investor or builder.
          </p>
        </div>
      )}

      {/* Owner Contacts (#2091) — scout+pro feature. Phone/email/mailing rows
          from the nightly home-PC enrichment batch (owner_contacts table).
          Free-tier sees the section header + upsell only; the backend never
          sends contact values to a locked viewer (see OwnerContactsPanel's
          doc comment — locked viewers get the explicit ProGate, never PII). */}
      <Suspense fallback={<div className="panel-loading" />}>
        <OwnerContactsPanel property={property} isPro={isPro} onUpgrade={onRequirePro} />
      </Suspense>

      {/* Building Details — populated nightly by SpatialEst deep enrichment.
          Each row is hidden individually when its source field is missing,
          so unenriched properties show no empty rows. The whole section
          collapses to nothing when no fields are present. */}
      {(() => {
        const building = property.building;
        // The helpers each of these used to go through (rawNumber/rawString)
        // are gone — the server coerces once (#1872). `?? null` keeps the
        // downstream `!== null` checks and formatters working unchanged.
        const beds = building?.bedrooms ?? null;
        const fullBaths = building?.fullBaths ?? null;
        const halfBaths = building?.halfBaths ?? null;
        const bedBath = formatBedBath(beds, fullBaths, halfBaths);
        const sqft = building?.totalSqft ?? null;
        const yearBuilt = building?.yearBuilt ?? null;
        const lotAcres = building?.lotSizeAcres ?? null;
        const lotSqft = building?.lotSizeSqft ?? null;
        const lotSize = formatLotSize(lotAcres, lotSqft);
        const style = building?.style ?? null;
        const heating = building?.heatingType ?? null;
        const foundation = building?.foundation ?? null;
        // The county-CAMA-first / OneMap-fallback precedence this used to
        // apply inline is now settled server-side (ADR 0035 §4).
        const landValue = property.valuation?.landValue ?? null;
        const buildingValue = property.valuation?.buildingValue ?? null;
        // #106 — condo parcels don't own land at the parcel level
        // (land_area=0 is the honest answer, not missing data). Hide
        // the Lot Size row entirely for them; the Condominium label on
        // Property Type already communicates "no individual lot."
        const landUseDesc = (building?.landUseDesc || "").toUpperCase();
        const isCondo = landUseDesc === "CONDOMINIUM";
        const showLotSize = lotSize && !isCondo;

        const hasAny =
          bedBath || sqft !== null || yearBuilt !== null || showLotSize ||
          style || heating || foundation || landValue !== null || buildingValue !== null;

        if (!hasAny) return null;
        return (
          <>
            <div className="section-label">Building Details</div>
            {bedBath && (
              <div className="detail-group">
                <label>Beds / Baths</label>
                <p>{bedBath}</p>
              </div>
            )}
            {sqft !== null && sqft > 0 && (
              <div className="detail-group">
                <label>Heated Sqft</label>
                <p>{Math.round(sqft).toLocaleString()}</p>
              </div>
            )}
            {showLotSize && (
              <div className="detail-group">
                <label>Lot Size</label>
                <p>{lotSize}</p>
              </div>
            )}
            {yearBuilt !== null && yearBuilt > 0 && (
              <div className="detail-group">
                <label>Year Built</label>
                <p>{yearBuilt}</p>
              </div>
            )}
            {style && (
              <div className="detail-group">
                <label>Style</label>
                <p>{style}</p>
              </div>
            )}
            {heating && (
              <div className="detail-group">
                <label>Heating</label>
                <p>{heating}</p>
              </div>
            )}
            {foundation && (
              <div className="detail-group">
                <label>Foundation</label>
                <p>{foundation}</p>
              </div>
            )}
            {(landValue !== null || buildingValue !== null) && (
              <div className="detail-row">
                {landValue !== null && landValue > 0 && (
                  <div className="detail-group">
                    <label>Land Value</label>
                    <p>{formatCurrency(landValue)}</p>
                  </div>
                )}
                {buildingValue !== null && buildingValue > 0 && (
                  <div className="detail-group">
                    <label>Building Value</label>
                    <p>{formatCurrency(buildingValue)}</p>
                  </div>
                )}
              </div>
            )}
          </>
        );
      })()}

      {/* Financial Info — never a bare $0/N/A: show the value, a clearly
          labeled derived figure, or an honest "unavailable" with a reason. */}
      <div className="section-label">Financial</div>
      {(() => {
        const assessed = resolveAssessedValue(property);
        const market = resolveMarketValue(property);
        const owed = resolveAmountOwed(property);
        // Only render the market row when it adds something the Assessed Value
        // row doesn't already say. resolveMarketValue falls back to the SAME
        // assessedValue when no CAMA estimate exists, so showing both in that
        // case prints one number twice under two different labels — which reads
        // as corroboration from two sources when it is a single source.
        const showMarket = market.state === "known";
        return (
          <div className="detail-row">
            <div className="detail-group">
              <label>Assessed Value</label>
              {isAllowedCounty ? (
                assessed.amount !== null ? (
                  <>
                    <p>{formatCurrency(assessed.amount)}</p>
                    {/* When there's no CAMA market estimate, the assessed figure
                        is the ONLY valuation a viewer gets — so the "this is not
                        market value" caveat has to land here, on the number they
                        will actually anchor their bid to. When a real estimate
                        DOES exist it gets its own labeled field carrying its own
                        provenance note, so repeating the caveat here would be
                        noise. */}
                    {market.state === "derived" && market.note && (
                      <p className="secondary field-note">{market.note}</p>
                    )}
                  </>
                ) : (
                  <p className="secondary">Unavailable — {assessed.note}</p>
                )
              ) : (
                <p className="secondary">
                  Outside your area —{" "}
                  <button type="button" className="link-button" onClick={handleOutsideAreaClick}>
                    {onPickCounty ? "pick your county to see this" : "upgrade to see this"}
                  </button>
                </p>
              )}
            </div>
            {showMarket && (
              <div className="detail-group">
                <label>Est. Market Value</label>
                {isAllowedCounty ? (
                  <>
                    <p>{formatCurrency(market.amount as number)}</p>
                    {market.note && <p className="secondary field-note">{market.note}</p>}
                  </>
                ) : (
                  <p className="secondary">
                    Outside your area —{" "}
                    <button type="button" className="link-button" onClick={handleOutsideAreaClick}>
                      {onPickCounty ? "pick your county to see this" : "upgrade to see this"}
                    </button>
                  </p>
                )}
              </div>
            )}
            <div className="detail-group">
              <label>Amount Due</label>
              {owed.amount !== null ? (
                <>
                  <p className="amount-due">{formatCurrency(owed.amount)}</p>
                  {owed.state === "derived" && (
                    <p className="secondary field-note">{owed.note}</p>
                  )}
                </>
              ) : (
                <p className="secondary">Unavailable — {owed.note}</p>
              )}
            </div>
            {/* Max-bid calculator (#3209). Hidden for out-of-area rows, whose
                value and debt figures are withheld. */}
            {isAllowedCounty && (
              <MaxBidCalculator
                propertyId={property.id}
                suggestedArv={resolveMarketValue(property).amount}
                referenceAmount={
                  property.sale?.currentBid && property.sale.currentBid > 0 ? property.sale.currentBid : owed.amount
                }
                referenceLabel={
                  property.sale?.currentBid && property.sale.currentBid > 0 ? "the current bid" : "the amount owed"
                }
              />
            )}
          </div>
        );
      })()}

      {/* Key Dates — when no real sale date is published yet (auction_date null
          and raw sale_date is a placeholder like "Sale date not yet set") but an
          upset-bid deadline IS on file, say so plainly instead of showing "N/A"
          next to a deadline (the 7819 Meadowdale confusion). */}
      <div className="section-label">Key Dates</div>
      {(() => {
        const dateRe = /\d{1,2}\/\d{1,2}\/\d{4}|\d{4}-\d{2}-\d{2}/;
        const rawSaleIsReal = !!saleDate && dateRe.test(saleDate);
        const display = rawSaleIsReal ? saleDate : property.auctionDate ? formatDate(property.auctionDate) : null;
        return (
          <div className="detail-group">
            <label>Sale / Auction Date</label>
            {display && property.sale?.listingStale && (
              <p className="secondary field-note" data-testid="sale-listing-stale">
                Unconfirmed. The foreclosing firm has stopped listing this sale
                {property.sale.listingMissedRuns
                  ? ` (missed in ${property.sale.listingMissedRuns} straight checks)`
                  : ""}
                . It may be cancelled, paused or resolved. Confirm with the court docket or the firm
                before relying on this date.
              </p>
            )}
            {display ? (
              <p>{display}</p>
            ) : (
              <p className="secondary">
                Sale date not yet published{upsetDeadline ? " — an upset-bid deadline is on file" : ""}
              </p>
            )}
          </div>
        );
      })()}
      {upsetDeadline && (
        <div className="detail-group">
          <label>Upset Bid Deadline</label>
          <p className="deadline">{upsetDeadline}</p>
          {upsetDeadlineEstimated && <p className="secondary">Estimated from the court docket: 10 days after the latest bid or report of sale, past weekends and court holidays. Confirm with the clerk before relying on it.</p>}
        </div>
      )}

      {/* Bid Activity */}
      {(openingBid != null || currentBid != null) && (
        <>
          <div className="section-label">Bid Activity</div>
          <div className="detail-row">
            {openingBid != null && (
              <div className="detail-group">
                <label>Opening Bid</label>
                <p>{formatCurrency(openingBid)}</p>
              </div>
            )}
            {currentBid != null && (
              <div className="detail-group">
                <label>Current Upset Bid</label>
                <p className="current-bid">{formatCurrency(currentBid)}</p>
              </div>
            )}
          </div>
          {premiumPct !== null && (
            <p className="bid-premium">
              Current bid exceeds opening by {premiumPct}%
            </p>
          )}
        </>
      )}

      {/* Sold but no final price — be honest rather than letting an opening bid
          read like the sale price. Live feeds drop a property the moment it
          sells and we have no historical sale-price source yet (Phase 5 / ROD). */}
      {(property.status === "sold" || property.status === "pending_confirmation") &&
        currentBid == null && (
          <p className="bid-note sold-no-price">
            Final sale price not published by the source — check county records.
          </p>
        )}

      {/* Court / Source */}
      {courtFile && (
        <div className="detail-group">
          <label>Court File #</label>
          <p>{courtFile}</p>
        </div>
      )}
      {source && (
        <div className="detail-group">
          <label>Source</label>
          <p className="secondary">{source}</p>
        </div>
      )}

      {/* Court Case — populated by sync-portal when court_file_number
          is known. Shows case header + party list + chronological
          action timeline so investors can assess case maturity at a
          glance. Hidden entirely when no portal data has been pulled. */}
      {(() => {
        const portal = portalCase;
        if (!portal) return null;
        return (
          <>
            <div className="section-label">Court Case</div>
            {portal.caseStatus && (
              <div className="detail-group">
                <label>Case Status</label>
                <p>{portal.caseStatus}</p>
              </div>
            )}
            {portal.caseType && (
              <div className="detail-group">
                <label>Case Type</label>
                <p>{portal.caseType}</p>
              </div>
            )}
            {portal.fileDate && (
              <div className="detail-group">
                <label>Filed</label>
                <p>{portal.fileDate}</p>
              </div>
            )}
            {portal.nextHearing && (
              <div className="detail-group">
                <label>Next Hearing</label>
                <p>{portal.nextHearing}</p>
              </div>
            )}
            {portal.parties && portal.parties.length > 0 && (() => {
              const sorted = sortParties(portal.parties!);
              const shown = showAllParties ? sorted : sorted.slice(0, PARTIES_COLLAPSED_COUNT);
              const hidden = sorted.length - shown.length;
              return (
                <div className="detail-group">
                  <label>Parties ({sorted.length})</label>
                  <table className="party-table">
                    <tbody>
                      {shown.map((p, i) => (
                        <tr key={`${p.name}-${i}`}>
                          <td className="party-name">{p.name}</td>
                          <td className="party-role">{p.role || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {sorted.length > PARTIES_COLLAPSED_COUNT && (
                    <button
                      type="button"
                      className="link-button"
                      onClick={() => setShowAllParties((v) => !v)}
                    >
                      {showAllParties ? "Show fewer" : `Show all ${sorted.length} parties (+${hidden})`}
                    </button>
                  )}
                </div>
              );
            })()}
            {portal.actions && portal.actions.length > 0 && (
              <div className="detail-group">
                <button
                  type="button"
                  className="timeline-toggle"
                  aria-expanded={timelineOpen}
                  onClick={() => setTimelineOpen((v) => !v)}
                >
                  <span>{timelineOpen ? "▾" : "▸"}</span> Action Timeline ({portal.actions.length})
                  {!timelineOpen && portal.actions[0] && (
                    <span className="timeline-latest">
                      {" "}· latest: {portal.actions[0].date} {portal.actions[0].description}
                    </span>
                  )}
                </button>
                {timelineOpen && (
                  <ol className="portal-actions">
                    {portal.actions.map((a, i) => (
                      <li key={`${a.date}-${i}`} className="portal-action">
                        <span className="portal-action-date">{a.date}</span>
                        <span className="portal-action-desc">{a.description}</span>
                      </li>
                    ))}
                  </ol>
                )}
              </div>
            )}
            {portal.lastLookupAt && (
              <p className="secondary portal-last-lookup">
                Last refreshed: {new Date(portal.lastLookupAt).toLocaleString()}
              </p>
            )}
          </>
        );
      })()}

      {/* Parties Named in Legal Notice (#916) — independent of the Court
          Case block above: this is the party as PUBLISHED in the notice
          (defendant/borrower/decedent), not Tyler's own case-party list, and
          a notice-promoted property frequently has this with NO portal case
          at all. Deliberately its own section, never blended into "Owner" —
          the notice party can differ from the tax-roll owner (an estate,
          "HEIRS OF", a post-transfer owner) and for probate may be deceased. */}
      {(() => {
        const noticeParties = property.noticeParties;
        if (!noticeParties?.length) return null;
        return (
          <div className="detail-group">
            <label>Parties Named in Legal Notice</label>
            <table className="party-table">
              <tbody>
                {noticeParties.map((p, i) => (
                  <tr key={`${p.name}-${i}`}>
                    <td className="party-name">{p.name}</td>
                    <td className="party-role">{p.role}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })()}

      {/* Every court case attached to this property when there are 2+ —
          one lead per timeshare resort, with its interval cases listed. */}
      <LinkedCasesSection linkedCases={property.linkedCases} isTimeshare={property.isTimeshare} />

      {/* Who's bidding here — upset bidders / purchasers from public court
          record (#575). Public (not Pro-gated). Renders nothing when no
          bidders are on file, which is most properties. */}
      <BiddersPanel propertyId={property.id} />

      {/* Bid Intelligence — Pro-tier feature (#157). Aggregate
          winning-bid premium for this property's zip. Free users see
          a blurred preview + upgrade CTA. Sits ABOVE Bid Timeline
          because the aggregate context primes the user to read the
          per-property history with the right benchmark in mind.

          AUDIT (#677): `isPro` here (includes scout) is correct, not an
          over-grant — GetBidIntelligence (bid_intelligence.go) already
          gives Scout the exact same stats as Pro server-side, no delay.
          This boolean only picks "real panel vs. free teaser"; it isn't
          the actual data gate. */}
      <Suspense fallback={<div className="panel-loading" />}>
        <BidIntelligencePanel property={property} isPro={isPro} onUpgrade={onRequirePro} />
      </Suspense>

      {/* Bid Timeline — paid feature; free users get the explicit ProGate
          (#188), never a mounted timeline under a blur.

          AUDIT (#677): `isPro` here (includes scout) is correct, not an
          over-grant — GetPropertyBids (bid_handler.go) already gives Scout
          the full timeline delayed bidHistoryScoutDelay (7 days) behind
          Pro/Team/admin's real-time feed. Excluding scout from this
          `isPro` check would hide the (correctly delayed) data Scout is
          actually entitled to, not tighten anything. */}
      <div className="section-label">Bid Timeline</div>
      <Suspense fallback={<div className="panel-loading" />}>
        {isPro ? (
          <BidTimeline propertyId={property.id} />
        ) : (
          <BidHistoryGate propertyId={property.id} onUpgrade={onRequirePro} />
        )}
      </Suspense>

      {/* Deal Analyzer */}
      <Suspense fallback={<div className="panel-loading" />}>
        <DealAnalyzer property={property} isPro={isPro} onUpgrade={onRequirePro} />
      </Suspense>

      {/* My Notes — only shown when this property is in user's watchlist */}
      {authed && isWatched && setStage && getStage && (
        <>
          <div className="section-label">Deal stage</div>
          <select
            className="deal-stage-select"
            aria-label="Deal stage"
            value={getStage(property.id) ?? ""}
            onChange={(e) => setStage(property.id, isDealStage(e.target.value) ? e.target.value : null)}
          >
            <option value="">No stage</option>
            {DEAL_STAGES.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </>
      )}

      {authed && isWatched && (
        <>
          <div className="section-label">My Notes</div>
          <textarea
            className="notes-editor"
            aria-label="My notes"
            value={notes}
            onChange={(e) => {
              setNotes(e.target.value);
              setNotesDirty(true);
            }}
            onBlur={() => notesDirty && saveNotes()}
            placeholder="Private notes on this property (only you can see these)…"
            rows={3}
          />
          {notesDirty && (
            <button className="notes-save-btn" onClick={saveNotes}>
              Save notes
            </button>
          )}
        </>
      )}

      {/* Contact log and follow-ups (#3208): signed-in users, any property. */}
      {authed && <ContactLog propertyId={property.id} />}

      {/* External Links */}
      <div className="section-label">Research Links</div>
      <div className="external-links">
        {/* County GIS / Polaris and SpatialEst are county-scoped. Hide
            the link entirely when we don't have a verified per-county
            URL for the property's county (#238). Showing a Mecklenburg
            URL for a Cabarrus parcel was producing 404s / wrong-parcel
            redirects. */}
        {(() => {
          const polarisUrl = buildPolarisUrl(property.parcelId, property.countyId);
          if (!polarisUrl) return null;
          return (
            <a
              href={polarisUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ext-link"
            >
              <span className="ext-icon">GIS</span>
              County GIS / Tax Records
            </a>
          );
        })()}
        {(() => {
          const seUrl = buildSpatialEstUrl(property.parcelId, property.countyId);
          if (!seUrl) return null;
          return (
            <a
              href={seUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="ext-link"
            >
              <span className="ext-icon">SE</span>
              SpatialEst Property Card
            </a>
          );
        })()}
        {courtFile && isOdysseyFormat(courtFile) && (
          <a
            href={buildOdysseyUrl()}
            target="_blank"
            rel="noopener noreferrer"
            className="ext-link"
          >
            <span className="ext-icon ext-icon--odyssey">ODY</span>
            <span className="ext-link-body">
              <span>NC Odyssey Court Records</span>
              <span className="ext-meta">
                Search by Case #: <code>{courtFile}</code>
                <button
                  type="button"
                  className="copy-btn"
                  onClick={handleCopyCaseNumber}
                  title="Copy case number"
                >
                  {copied ? "✓ Copied" : "Copy"}
                </button>
              </span>
            </span>
          </a>
        )}
        {courtFile && !isOdysseyFormat(courtFile) && (
          // Non-Odyssey format (e.g. Gaston's "25 M 388") — show the number
          // without a dead eCourts link.
          <div className="ext-link ext-link--static">
            <span className="ext-icon ext-icon--odyssey">CF</span>
            <span className="ext-link-body">
              <span>County file #: <code>{courtFile}</code></span>
              <span className="ext-meta">Not searchable in NC eCourts</span>
            </span>
          </div>
        )}
        {/* Zillow — only when the address is deliverable (real street + city,
            not a legal description). A bare street or legal-desc resolves to the
            wrong city; fewer links, all correct. (#488 / live review) */}
        {isDeliverableAddress(property) && (
          <a
            href={buildZillowUrl(property)}
            target="_blank"
            rel="noopener noreferrer"
            className="ext-link"
            title={buildZillowLabel(property)}
          >
            <span className="ext-icon">Z</span>
            {buildZillowLabel(property)}
          </a>
        )}
        {/* Street View, Redfin and a Maps pin (#3212): the tabs an investor opens
            for every property. Street View and Maps use coordinates only (an
            address search can land in the wrong city) and are hidden when the
            row has none. */}
        {streetViewUrl && (
          <a href={streetViewUrl} target="_blank" rel="noopener noreferrer" className="ext-link" title="Open Google Street View">
            <span className="ext-icon">SV</span>
            Street View
          </a>
        )}
        {mapsUrl && (
          <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="ext-link" title="Open in Google Maps (satellite is one click away)">
            <span className="ext-icon">GM</span>
            Google Maps
          </a>
        )}
        {isDeliverableAddress(property) && (
          <a href={buildRedfinUrl(property)} target="_blank" rel="noopener noreferrer" className="ext-link" title="Search this address on Redfin">
            <span className="ext-icon">R</span>
            Redfin
          </a>
        )}
      </div>

      {/* BPO Report */}
      {property.bpoUrl && (
        <a
          href={property.bpoUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="bpo-link"
        >
          View BPO Report
        </a>
      )}
    </div>
  );
};

export default PropertyDetail;
